<template>
  <div
    class="container-fluid flex-grow-1 d-flex flex-column px-0 overflow-auto mx-100"
    style="min-height: 0"
  >
    <LiveAnnouncer />

    <WarningModal
      ref="stepExitWarningModal"
      modal-id="step-exit-warning-modal"
      :title="stepExitModalTitle"
      :body="stepExitModalBody"
      cancel-label="Stay on step"
      confirm-label="Proceed anyway"
      @proceed="onStepExitProceed"
      @dismiss="onStepExitDismiss"
    />

    <WarningModal
      ref="storyRestartWarningModal"
      modal-id="story-restart-warning-modal"
      :title="storyRestartModalTitle"
      :body="storyRestartModalBody"
      cancel-label="Cancel"
      confirm-label="Restart"
      confirm-button-class="btn-outline-danger"
      @proceed="onStoryRestartProceed"
    />

    <!-- Lab Attempt Status Banner -->

    <!-- Loading screen -->
    <div
      v-if="!storyService"
      class="flex-grow-1 d-flex px-0 px-lg-4 justify-content-center align-items-center"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else class="flex-grow-1 d-flex flex-column p-0 px-lg-4 py-lg-3">
      <div class="row flex-grow-1 d-flex flex-column flex-lg-row gx-0 gx-lg-4">
        <div class="col-lg mb-lg-0 flex-grow-1">
          <section
            id="instructions-panel"
            data-tour="workbook-panel"
            tabindex="-1"
            aria-labelledby="instructions-heading"
            class="card shadow h-100 text-start custom-rounded"
          >
            <div
              class="card-header d-flex justify-content-between align-items-center bg-transparent border-0 flex-wrap gap-2"
            >
              <div
                class="d-flex align-items-center justify-content-between flex-grow-1 min-w-0 gap-2 gap-md-3"
              >
                <h2
                  id="instructions-heading"
                  class="h6 mb-0 fw-semibold flex-shrink-0"
                >
                  Workbook
                </h2>
                <WorkbookTocOffcanvas
                  ref="workbookTocOffcanvasRef"
                  id="workbookTocOffcanvas"
                  :passages="tocPassages"
                  :current-passage-id="context?.current || ''"
                  :visited-passage-ids="visitedPassageIds"
                  @select="handleTocSelect"
                />
              </div>
              <!-- Mobile: Lab Area offcanvas and tour trigger -->
              <div v-if="inMobileView" class="d-flex gap-2 align-items-center">
                <LabAreaOffcanvas
                  ref="labAreaOffcanvasRef"
                  id="labAreaOffcanvas"
                >
                  <div>
                    <LabAreaRenderer
                      :lab-id="labId"
                      :context="context"
                      :current-attempt="currentLabAttempt"
                      :showDebugUI="showDebugUI"
                      :showSimpleUI="showSimpleUI"
                      :lab-area-locked="labAreaLockOverlayActive"
                      @scrollToOverview="scrollToOverview"
                      @lab-data-updated="handleLabDataUpdate"
                    />
                  </div>
                </LabAreaOffcanvas>
                <button
                  type="button"
                  class="btn btn-outline-dark fw-semibold btn-sm border-2"
                  title="Start Lab Area tour"
                  aria-label="Start Lab Area tour"
                  @click="startLabAreaTour"
                >
                  <span class="bi bi-question-circle" aria-hidden="true"></span>
                  Tour
                </button>
              </div>
            </div>
            <div
              class="card-body d-flex flex-column h-100 p-lg-4 pt-0"
              data-tour="workbook-story"
            >
              <!-- Show loading spinner if using datastore and entry not loaded -->
              <div
                v-if="useDatastore && !entryLoaded"
                class="d-flex justify-content-center align-items-center flex-grow-1"
              >
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
              <!-- Show StoryBoard when entry is loaded or when not using datastore -->
              <StoryBoard
                v-else-if="
                  (!useDatastore || entryLoaded) && storyService && context
                "
                :storyService="storyService"
                :storyContext="context"
                :classMap="{
                  choiceItem: `btn btn-primary w-100 text-start ${savingProgress ? 'disabled' : ''}`,
                  choiceList: 'list-unstyled m-0'
                }"
                @restart="onStoryRestartRequest"
                @back="isFirstPassage ? null : storyService.back()"
                @choose="handleStoryChoice"
              >
                <template v-slot:choices>
                  <button
                    v-if="savingProgress"
                    class="btn btn-primary mb-2 w-100 text-start disabled mb-3 text-center"
                  >
                    <span
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span class="visually-hidden">Saving...</span>
                  </button>
                </template>
                <template v-slot:customBlock="{ block }">
                  <button
                    v-if="
                      block.name === 'skip-to-measurements' && hasCompletedLab
                    "
                    @click="handleSkipToMeasurements(block.skipTo)"
                    class="btn btn-outline-primary btn-sm"
                  >
                    Skip to measurements
                  </button>
                </template>
              </StoryBoard>
            </div>
          </section>
        </div>
        <section
          v-if="!inMobileView"
          id="lab-area-panel"
          tabindex="-1"
          aria-labelledby="lab-area-heading"
          class="col-lg-8 mb-lg-0 z-1 position-relative"
        >
          <div
            ref="labAreaCard"
            class="card shadow bg-light d-flex flex-column position-sticky top-0-padding-3 h-100"
            :class="{ 'border-0 rounded-0': inMobileView }"
          >
            <div
              class="card-header d-flex justify-content-between align-items-center bg-transparent border-0"
            >
              <h2 id="lab-area-heading" class="card-title h6 mb-0 fw-semibold">
                Lab Area
              </h2>
              <div class="d-flex gap-2 align-items-center">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-dark fw-semibold border-2"
                  title="Start Lab Area tour"
                  aria-label="Start Lab Area tour"
                  @click="startLabAreaTour"
                >
                  <span class="bi bi-question-circle" aria-hidden="true"></span>
                  Tour
                </button>
                <button
                  class="btn btn-sm btn-outline-secondary border-2 fw-semibold text-dark"
                  data-tour="simple-ui-toggle"
                  @click="toggleSimpleUI"
                >
                  <span class="bi bi-eye me-1 fs-6"></span>
                  {{
                    showSimpleUI ? "Interactive Interface" : "Simple Interface"
                  }}
                </button>
              </div>
            </div>
            <div class="card-body p-3 pt-1 flex-grow-1">
              <LabAreaRenderer
                :lab-id="labId"
                :context="context"
                :current-attempt="currentLabAttempt"
                :showDebugUI="showDebugUI"
                :showSimpleUI="showSimpleUI"
                :lab-area-locked="labAreaLockOverlayActive"
                @scrollToOverview="scrollToOverview"
                @lab-data-updated="handleLabDataUpdate"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { StoryBoard } from "@osuecampus/branching-stories/vue";
import { mapActions, mapGetters, mapState } from "vuex";
import DataStoreAPI from "@/helpers/DatastoreAPI";
import LabAreaOffcanvas from "@/components/activity/LabAreaOffcanvas.vue";
import LabAreaRenderer from "@/components/activity/LabAreaRenderer.vue";
import WorkbookTocOffcanvas from "@/components/activity/WorkbookTocOffcanvas.vue";
import LiveAnnouncer from "@/components/core/LiveAnnouncer.vue";
import { useLiveAnnouncer } from "@/composables/useLiveAnnouncer";
import {
  useLabAreaTour,
  hasViewedLabAreaTour
} from "@/composables/useLabAreaTour";
import { useLabPersistence } from "@/composables/useLabPersistence";
import { useLabCompletion } from "@/composables/useLabCompletion";
import { useStoryEngine } from "@/composables/useStoryEngine";
import { getExitWarning, ACTIVE_LAB_ID } from "@/labRegistry.js";
import WarningModal from "@/components/activity/WarningModal.vue";

const { announce } = useLiveAnnouncer();

export default {
  name: "App",
  mixins: [DataStoreAPI],
  components: {
    StoryBoard,
    LabAreaOffcanvas,
    LabAreaRenderer,
    WorkbookTocOffcanvas,
    LiveAnnouncer,
    WarningModal
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const instance = getCurrentInstance();
    const getUpdateEntry = () => instance?.proxy?.updateEntry;

    const persistence = useLabPersistence({ store, getUpdateEntry });

    let completion;
    const story = useStoryEngine({
      store,
      router,
      onAfterStateChange: (snapshot) => {
        persistence.saveProgress();
        completion.checkLabCompletion(snapshot.context);
      }
    });

    completion = useLabCompletion({
      store,
      router,
      reinitializeStory: () => story.initializeStory()
    });

    return {
      savingProgress: persistence.savingProgress,
      saveProgress: persistence.saveProgress,
      handleLabDataUpdate: persistence.handleLabDataUpdate,
      toggleSimpleUI: persistence.toggleSimpleUI,
      storyService: story.storyService,
      storyDataLoaded: story.storyDataLoaded,
      stories: story.stories,
      initializeStory: story.initializeStory
    };
  },
  data() {
    return {
      /** While true, lab lock overlay is hidden so Intro.js can highlight controls. */
      labTourActive: false,
      pendingStoryChoice: null,
      pendingTocPassageId: null,
      stepExitModalTitle: "Incomplete measurements",
      stepExitModalBody: "",
      storyRestartModalTitle: "Restart story?",
      storyRestartModalBody:
        "Your progress for this attempt will be lost, including your place in the workbook and lab measurements. Are you sure you want to restart?"
    };
  },
  computed: {
    /** Build-time lab id from env; must match `src/data/labs/<id>/` and `src/labs/<id>/`. */
    labId() {
      return ACTIVE_LAB_ID;
    },
    ...mapGetters([
      "isAdmin",
      "isDebugMode",
      "context",
      "mapToDatastore",
      "useDatastore",
      "theme",
      "getCurrentLabAttempt",
      "maxAttempts",
      "labAttempts",
      "canStartNewAttempt",
      "showDebugUI",
      "showSimpleUI",
      "inMobileView",
      "labManifest",
      "storyEngineActive"
    ]),
    ...mapGetters("datastore", ["entryLoaded", "entry"]),
    ...mapState("datastore", ["entryLoaded"]),
    currentLabAttempt() {
      return this.getCurrentLabAttempt;
    },
    labMeasurements() {
      // Get measurements from current lab attempt
      return this.currentLabAttempt?.measurements || [];
    },

    /**
     * Get scenes array from production draftData
     */
    productionScenes() {
      return this.stories[this.labId]?.draft || [];
    },
    /** Ordered list for workbook table of contents (all draft passages). */
    tocPassages() {
      return this.productionScenes.map((p) => ({
        id: p.id,
        label: p.progressLabel || p.title || p.id
      }));
    },
    /** From `context.flags`; maintained in useStoryEngine for TOC locking. */
    visitedPassageIds() {
      const raw = this.context?.flags?.visitedPassageIds;
      return Array.isArray(raw) ? raw : [];
    },
    hasCompletedLab() {
      return this.labAttempts.some((attempt) => attempt.isComplete === true);
    },
    isFirstPassage() {
      return this.context?.current === "start" || !this.context?.current;
    },
    isLabAreaUnlocked() {
      if (!this.context?.current) return false;
      if (this.context.flags?.labAreaUnlocked === true) return true;
      const m = this.labManifest;
      const unlockId = m?.labAreaUnlockPassageId;
      const ids = m?.passageIds;
      if (!unlockId || !ids?.length) return false;
      const step1Idx = ids.indexOf(unlockId);
      const curIdx = ids.indexOf(this.context.current);
      if (step1Idx < 0 || curIdx < 0) return false;
      return curIdx >= step1Idx;
    },
    labAreaLocked() {
      return !this.isLabAreaUnlocked;
    },
    /** Workbook lock; suppressed during guided tour or when debug UI is visible. */
    labAreaLockOverlayActive() {
      return this.labAreaLocked && !this.labTourActive && !this.showDebugUI;
    },
    shouldAutoStartLabTour() {
      return this.storyEngineActive && !hasViewedLabAreaTour(this.labId);
    }
  },
  watch: {
    entryLoaded: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          this.$nextTick(() => {
            this.initializeStory();
            announce("Entry loaded", "assertive");
          });
        }
      }
    },
    shouldAutoStartLabTour: {
      immediate: true,
      handler(ready) {
        if (ready) {
          this.$nextTick(() => {
            setTimeout(() => this.startLabAreaTour(), 500);
          });
        }
      }
    }
  },
  methods: {
    ...mapActions(["setInMobileView", "setStoryEngineActive"]),
    startLabAreaTour() {
      const { startTour } = useLabAreaTour({
        labId: this.labId,
        inMobileView: this.inMobileView,
        showSimpleUI: this.showSimpleUI,
        labAreaOffcanvasRef: this.$refs.labAreaOffcanvasRef,
        onTourStart: () => {
          this.labTourActive = true;
        },
        onTourEnd: () => {
          this.labTourActive = false;
        }
      });
      startTour();
    },
    handleSkipToMeasurements(passageId) {
      this.storyService.jumpTo(passageId);
    },
    handleStoryChoice(choice) {
      if (!this.storyService || this.savingProgress) return;
      const warning = getExitWarning({
        labId: this.labId,
        passageId: this.context?.current,
        measurements: this.labMeasurements
      });
      if (warning) {
        this.pendingTocPassageId = null;
        this.pendingStoryChoice = choice;
        this.stepExitModalTitle = warning.title;
        this.stepExitModalBody = warning.body;
        this.$nextTick(() => {
          this.$refs.stepExitWarningModal?.showModal();
        });
        return;
      }
      this.storyService.choose(choice.to);
    },
    onStepExitProceed() {
      const pendingToc = this.pendingTocPassageId;
      const choice = this.pendingStoryChoice;
      this.pendingTocPassageId = null;
      this.pendingStoryChoice = null;
      if (pendingToc && this.storyService) {
        this.storyService.jumpTo(pendingToc);
        this.$refs.workbookTocOffcanvasRef?.hide();
        return;
      }
      if (choice && this.storyService) {
        this.storyService.choose(choice.to);
      }
    },
    onStepExitDismiss() {
      this.pendingStoryChoice = null;
      this.pendingTocPassageId = null;
    },
    handleTocSelect({ id: targetId }) {
      if (!this.storyService || this.savingProgress) return;
      const unlocked =
        targetId === this.context?.current ||
        this.visitedPassageIds.includes(targetId);
      if (!unlocked) return;
      if (targetId === this.context?.current) {
        this.$refs.workbookTocOffcanvasRef?.hide();
        return;
      }
      const warning = getExitWarning({
        labId: this.labId,
        passageId: this.context?.current,
        measurements: this.labMeasurements
      });
      if (warning) {
        this.pendingStoryChoice = null;
        this.pendingTocPassageId = targetId;
        this.stepExitModalTitle = warning.title;
        this.stepExitModalBody = warning.body;
        this.$nextTick(() => {
          this.$refs.stepExitWarningModal?.showModal();
        });
        return;
      }
      this.storyService.jumpTo(targetId);
      this.$refs.workbookTocOffcanvasRef?.hide();
    },
    onStoryRestartRequest() {
      if (!this.storyService || this.savingProgress) return;
      this.$nextTick(() => {
        this.$refs.storyRestartWarningModal?.showModal();
      });
    },
    onStoryRestartProceed() {
      this.storyService?.restart();
    },
    scrollToOverview() {
      if (this.$refs.labAreaCard) {
        this.$refs.labAreaCard.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    },
    handleWindowResize() {
      const bsBreakpointLg = 992;
      const windowWidth = window.innerWidth || 0;
      if (windowWidth < bsBreakpointLg) {
        this.setInMobileView(true);
      } else {
        this.setInMobileView(false);
      }
    }
  },
  mounted() {
    this.initializeStory();
    this.handleWindowResize();
    window.addEventListener("resize", this.handleWindowResize);
  },
  beforeUnmount() {
    if (this.storyService) this.storyService.stop();
    this.setStoryEngineActive(false);
    window.removeEventListener("resize", this.handleWindowResize);
  }
};
</script>
<style scoped>
.top-0-padding-3 {
  top: 1rem;
}
.card.custom-rounded {
  border-radius: 0 !important;
}
@media (min-width: 992px) {
  .card.custom-rounded {
    border-radius: var(--bs-border-radius) !important;
  }
}
</style>
